import { z } from 'zod';

import { createAgentDefinitionSchema } from '@repo/shared-types';

import type { AgentDefinition, CreateAgentDefinitionDto } from '@repo/shared-types';

const EXPORT_VERSION = 1;

/**
 * On-disk JSON shape. `agents[].custom_tool_ids` references custom_tools UUIDs
 * in the source environment — those UUIDs must also exist in the target
 * environment for the import to succeed unchanged. If they don't, the
 * importer will leave them in and the user will need to re-pick custom tools
 * in the edit modal after import.
 */
export const agentDefinitionsBundleSchema = z.object({
  version: z.literal(EXPORT_VERSION),
  exported_at: z.string().datetime(),
  agents: z.array(createAgentDefinitionSchema).min(1, 'Bundle must contain at least one agent'),
});

export type AgentDefinitionsBundle = z.infer<typeof agentDefinitionsBundleSchema>;

/**
 * Strip server-side fields so the agent can be re-created cleanly in another
 * environment via POST /agent-definitions.
 */
export function toCreateDto(agent: AgentDefinition): CreateAgentDefinitionDto {
  return {
    name: agent.name,
    role: agent.role,
    prompt_template: agent.prompt_template,
    welcome_message: agent.welcome_message ?? undefined,
    tools: agent.tools,
    custom_tool_ids: agent.custom_tool_ids,
    voice_name: agent.voice_name ?? undefined,
    advanced: agent.advanced,
    handoff_conditions: agent.handoff_conditions ?? undefined,
    is_active: agent.is_active,
  };
}

export function buildBundle(agents: AgentDefinition[]): AgentDefinitionsBundle {
  return {
    version: EXPORT_VERSION,
    exported_at: new Date().toISOString(),
    agents: agents.map(toCreateDto),
  };
}

export function downloadJson(filename: string, payload: unknown): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Parse a file's text. Accepts either a full bundle ({ version, exported_at, agents })
 * or a single bare agent object (auto-wrapped). Anything else fails validation.
 */
export function parseBundle(rawText: string): AgentDefinitionsBundle {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawText);
  } catch (err) {
    throw new Error(`Invalid JSON: ${(err as Error).message}`);
  }

  // Auto-wrap single-agent shape
  if (
    parsed &&
    typeof parsed === 'object' &&
    !Array.isArray(parsed) &&
    !('agents' in parsed) &&
    'name' in parsed &&
    'prompt_template' in parsed
  ) {
    parsed = {
      version: EXPORT_VERSION,
      exported_at: new Date().toISOString(),
      agents: [parsed],
    };
  }

  // Auto-wrap bare array shape
  if (Array.isArray(parsed)) {
    parsed = {
      version: EXPORT_VERSION,
      exported_at: new Date().toISOString(),
      agents: parsed,
    };
  }

  const result = agentDefinitionsBundleSchema.safeParse(parsed);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') || '(root)';
    throw new Error(`Validation failed at ${path}: ${first?.message}`);
  }
  return result.data;
}

export async function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error(reader.error?.message ?? 'Failed to read file'));
    reader.readAsText(file);
  });
}
